/**
 * Tiny request deduplication + short-TTL response cache.
 *
 * Purpose
 * -------
 * Many independent components on the same page call the same GET endpoint
 * (`/api/settings/agency-profile`, `/api/cms/settings`, `/api/cms/testimonials`,
 * `/api/cms/pages/public/<slug>`). Each call costs a round-trip on production
 * (~150-300 ms) and they all return identical data. This helper:
 *
 *   1. **In-flight dedup** — if a fetch for URL X is already pending, every
 *      subsequent caller for X within that window receives the SAME promise.
 *   2. **Short-TTL response cache** — once the promise resolves, the parsed
 *      JSON body is cached for 60 s. Any caller within that window gets the
 *      cached value synchronously (no network).
 *
 * Constraints
 * -----------
 *   • Zero dependencies (no React Query / SWR).
 *   • Additive — replacing `fetch(url).then(r => r.json())` with
 *     `dedupedFetchJson(url)` is a one-for-one swap, identical return shape.
 *   • Only safe for *idempotent* requests (GET). The helper exposes a method
 *     hint so non-GET callers automatically bypass the cache.
 *   • Cache is per-page-load (Map in module scope) — no localStorage, no
 *     stale state across navigations between separate browser sessions.
 *   • `invalidate(url)` lets writers (admin pages saving settings) clear
 *     the cache entry so the next read fetches fresh.
 *
 * Failure mode
 * ------------
 * On non-OK responses or exceptions, the cache entry is NOT stored — the
 * caller sees the same error they would have seen with a plain fetch.
 */

const TTL_MS = 60 * 1000;
const inflight = new Map(); // url -> Promise<jsonBody>
const cache = new Map();    // url -> { ts: number, body: any }

const isFresh = (entry) => entry && (Date.now() - entry.ts) < TTL_MS;

const _cacheableKey = (url) => {
  // Strip the origin so calls to https://x/api/foo and /api/foo dedup to one
  // key — they hit the same backend resource. Preserves query string.
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname + u.search;
  } catch {
    return url;
  }
};

/**
 * Deduplicated GET fetch that returns the parsed JSON body.
 *
 * @param {string} url
 * @param {object} [options]   passed through to fetch() if a network call is needed
 * @returns {Promise<any>}     parsed JSON (or null on a 4xx/5xx that returns no body)
 */
export const dedupedFetchJson = (url, options = {}) => {
  // Non-GET → bypass cache + dedup entirely. This is a tiny perf helper, not
  // a write-through cache. Writes should call invalidate() afterwards.
  const method = (options.method || 'GET').toUpperCase();
  if (method !== 'GET') {
    return fetch(url, options).then((r) => (r.ok ? r.json() : null));
  }

  const key = _cacheableKey(url);

  // 1. Fresh cache hit → return immediately, no network.
  const cached = cache.get(key);
  if (isFresh(cached)) {
    return Promise.resolve(cached.body);
  }

  // 2. In-flight dedup → join the existing promise.
  if (inflight.has(key)) {
    return inflight.get(key);
  }

  // 3. Issue the request, record both the in-flight promise and the eventual
  //    body (on success). Note: JWT-staleness 401s are caught globally by
  //    `utils/fetchInterceptor.js` — no special-casing needed here.
  const promise = fetch(url, options)
    .then(async (r) => {
      if (!r.ok) return null;
      const body = await r.json();
      cache.set(key, { ts: Date.now(), body });
      return body;
    })
    .catch(() => null)
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
};

/**
 * Clear a cached entry. Call after a successful PUT/PATCH/POST/DELETE that
 * mutates the same resource so the next read fetches fresh data.
 */
export const invalidateDedupCache = (url) => {
  if (!url) {
    cache.clear();
    inflight.clear();
    return;
  }
  const key = _cacheableKey(url);
  cache.delete(key);
  inflight.delete(key);
};

export default dedupedFetchJson;
